/**
 * Main-thread client for the tile-parse Web Worker.
 *
 * Wraps a Worker with request-ID correlation so concurrent tile parse
 * requests (up to 9 tiles start simultaneously during initial load) resolve
 * independently. Provides reconstruction helpers that convert the worker's
 * flat typed arrays back into live `PathNode[]` / `Bvh` objects on the main
 * thread in O(N) — no binary parsing occurs here.
 */

import { vec3 } from 'gl-matrix';
import { PathNode } from '../navigation-mesh/PathNode';
import { BlasWorkerResult, NavmeshWorkerResult } from './TileParseWorker';
import { Bvh } from '../spatial/bvh/Bvh';

export type { NavmeshWorkerResult, BlasWorkerResult };

// --- Reconstruction helpers ---

/**
 * Reconstruct PathNode objects from a worker result. O(N+E) — pure array
 * reads, no binary parsing. Direct neighbour push (not addNeighbour) is safe
 * because the worker serialised the already-bidirectional neighbour lists
 * produced by NavigationMeshReader.
 */
export function reconstructNavmeshNodes(result: NavmeshWorkerResult): PathNode[] {
  const { nodeCount, bounds, ids, refData, refLengths, neighborCounts, neighborIndices } = result;
  const decoder = new TextDecoder();
  const nodes: PathNode[] = new Array(nodeCount);

  let refOff = 0;
  for (let i = 0; i < nodeCount; i++) {
    const min = vec3.fromValues(bounds[i * 6], bounds[i * 6 + 1], bounds[i * 6 + 2]);
    const max = vec3.fromValues(bounds[i * 6 + 3], bounds[i * 6 + 4], bounds[i * 6 + 5]);
    const node = new PathNode(min, max);
    node.id = ids[i];
    const refLen = refLengths[i];
    if (refLen > 0) {
      node.reference = decoder.decode(refData.subarray(refOff, refOff + refLen));
    }
    refOff += refLen;
    nodes[i] = node;
  }

  let nOff = 0;
  for (let i = 0; i < nodeCount; i++) {
    const count = neighborCounts[i];
    const node = nodes[i];
    for (let j = 0; j < count; j++) {
      const ni = neighborIndices[nOff++];
      if (ni >= 0) node.neighbours.push(nodes[ni]);
    }
  }

  return nodes;
}

/**
 * Wrap a worker-built BLAS payload as a `Bvh`. All five typed arrays are
 * already populated (transferred zero-copy from the worker); we just stitch
 * them together with a `rootBounds` object. No extra allocations except the
 * outer `Bvh` literal.
 */
export function reconstructBvh(result: BlasWorkerResult): Bvh {
  return {
    nodeCount: result.nodeCount,
    nodeBounds: result.nodeBounds,
    nodeLeftFirst: result.nodeLeftFirst,
    nodeTriCount: result.nodeTriCount,
    triIds: result.triIds,
    positions: result.positions,
    indices: result.indices,
    rootBounds: {
      min: result.rootMin,
      max: result.rootMax,
    },
  };
}

// --- Worker client ---

type PendingRequest = { resolve: (data: unknown) => void; reject: (err: Error) => void };

/** Minimal interface so tests can inject a mock without a real Worker. */
export interface WorkerLike {
  postMessage(message: unknown, transfer: ArrayBuffer[]): void;
  onmessage: ((e: { data: unknown }) => void) | null;
  onerror: ((e: { message?: string }) => void) | null;
  terminate(): void;
}

export class TileParseWorkerClient {
  private nextId = 0;
  private pending = new Map<number, PendingRequest>();

  constructor(private readonly worker: WorkerLike) {
    worker.onmessage = (e: { data: unknown }) => {
      const msg = e.data as { reqId: number; type: string } & Record<string, unknown>;
      const entry = this.pending.get(msg.reqId);
      if (!entry) return;
      this.pending.delete(msg.reqId);
      entry.resolve(msg);
    };
    worker.onerror = (e: { message?: string }) => {
      const err = new Error(e.message ?? 'TileParseWorker error');
      for (const entry of this.pending.values()) entry.reject(err);
      this.pending.clear();
    };
  }

  parseNavmesh(buffer: ArrayBuffer): Promise<NavmeshWorkerResult> {
    return new Promise((resolve, reject) => {
      const reqId = this.nextId++;
      this.pending.set(reqId, { resolve: resolve as (d: unknown) => void, reject });
      this.worker.postMessage({ reqId, type: 'parseNavmesh', buffer }, [buffer]);
    });
  }

  /**
   * Build a BLAS on the worker thread from (positions, indices). Both buffers
   * are TRANSFERRED — the caller must not retain references after the call.
   * The worker returns them back inside the result so the reconstructed `Bvh`
   * wraps the same buffers (zero-copy). Used to offload per-tile terrain BLAS
   * construction — keeps the main thread responsive when many tiles arrive
   * back-to-back during fast flight.
   */
  buildBlas(positions: Float32Array, indices: Uint32Array | Uint16Array): Promise<BlasWorkerResult> {
    return new Promise((resolve, reject) => {
      const reqId = this.nextId++;
      this.pending.set(reqId, { resolve: resolve as (d: unknown) => void, reject });
      this.worker.postMessage({ reqId, type: 'buildBlas', positions, indices }, [
        positions.buffer as ArrayBuffer,
        indices.buffer as ArrayBuffer,
      ]);
    });
  }

  terminate(): void {
    const err = new Error('TileParseWorkerClient terminated');
    for (const entry of this.pending.values()) entry.reject(err);
    this.pending.clear();
    this.worker.terminate();
  }
}
